import { useForm } from "@inertiajs/react";
import { tripPath } from "@travily/routes";
import type TripsEdit from "@travily/types/pages/TripsEdit";
import type { Selection } from "@ui/primitives/shared";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { AddPersonSheet } from "@/components/AddPersonSheet/AddPersonSheet";
import { RoutedModal } from "@/components/RoutedModal";
import { useImageUploadField } from "@/hooks/use-image-upload-field";
import { useLocationSearch } from "@/hooks/use-location-search";
import { TripModal } from "./components/TripModal";

const TripEditPage = ({
  sqid,
  location_name,
  start_date,
  end_date,
  description,
  photo,
  family_members,
  selected_member_ids,
}: TripsEdit) => {
  const path = tripPath(sqid);
  const [addPersonPresented, setAddPersonPresented] = useState(false);

  const form = useForm({
    location_mapbox_id: null as string | null,
    start_date: start_date as string | null,
    end_date: end_date as string | null,
    description: description ?? "",
    photo_signed_id: null as string | null,
    member_ids: selected_member_ids,
    remove_photo: false,
  })
    .withPrecognition("patch", path)
    .setValidationTimeout(400);

  const formRef = useRef(form);
  formRef.current = form;

  const locationSearch = useLocationSearch();
  const photoField = useImageUploadField({
    initialPreviewUrl: photo?.src ?? null,
    onUploaded: (signedId) => {
      formRef.current.setData("photo_signed_id", signedId);
      formRef.current.setData("remove_photo", false);
    },
    onRemoved: () => {
      formRef.current.setData("photo_signed_id", null);
      formRef.current.setData("remove_photo", true);
    },
  });

  // Pre-populate location autocomplete with existing value
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount
  useEffect(() => {
    locationSearch.setInitialValue({ id: "existing", name: location_name });
  }, []);

  const handleLocationSelectionChange = useCallback(
    (id: string | null) => {
      locationSearch.onSelectionChange(id);
      formRef.current.setData("location_mapbox_id", id);
      formRef.current.validate("location_mapbox_id");
    },
    [locationSearch.onSelectionChange],
  );

  const handleStartDateChange = useCallback((value: string | null) => {
    formRef.current.setData("start_date", value);
    formRef.current.validate("start_date");
  }, []);

  const handleEndDateChange = useCallback((value: string | null) => {
    formRef.current.setData("end_date", value);
  }, []);

  const handleDescriptionChange = useCallback((value: string) => {
    formRef.current.setData("description", value);
    formRef.current.validate("description");
  }, []);

  const handleMembersChange = useCallback(
    (keys: Selection) => {
      const ids = keys === "all" ? family_members.map((member) => member.id) : [...keys].map(Number);
      formRef.current.setData("member_ids", ids);
      formRef.current.validate("member_ids");
    },
    [family_members],
  );

  return (
    <RoutedModal presentation="page" size="sm">
      {({ close, reload }) => (
        <>
          <TripModal
            mode="edit"
            close={close}
            locationSearch={{
              inputValue: locationSearch.inputValue,
              isLoading: locationSearch.isLoading,
              sections: locationSearch.sections,
              selectedItem: locationSearch.selectedItem,
              onInputChange: locationSearch.onInputChange,
              onSelectionChange: handleLocationSelectionChange,
            }}
            locationError={form.invalid("location_mapbox_id") ? form.errors.location_mapbox_id : undefined}
            startDate={form.data.start_date}
            endDate={form.data.end_date}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            dateError={
              form.invalid("start_date") || form.invalid("end_date")
                ? form.errors.start_date || form.errors.end_date
                : undefined
            }
            description={form.data.description}
            onDescriptionChange={handleDescriptionChange}
            descriptionError={form.invalid("description") ? form.errors.description : undefined}
            members={family_members}
            selectedMemberKeys={new Set(form.data.member_ids.map(String))}
            onSelectedMembersChange={handleMembersChange}
            onAddPerson={() => setAddPersonPresented(true)}
            membersError={form.invalid("member_ids") ? form.errors.member_ids : undefined}
            photoPreviewSrc={photoField.previewUrl ?? undefined}
            onPhotoFileSelect={photoField.onFileSelect}
            onPhotoRemove={photoField.onRemove}
            onSubmit={() => {
              formRef.current.patch(path, {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => close(),
              });
            }}
            isPending={form.processing}
          />
          <AddPersonSheet
            onAdded={(personId) => {
              formRef.current.setData("member_ids", [...formRef.current.data.member_ids, personId]);
              // The person POST's redirect refreshes the base page, not this
              // modal's props — refetch them or the new tile never appears.
              reload({ only: ["family_members"] });
            }}
            onPresentedChange={setAddPersonPresented}
            presented={addPersonPresented}
          />
        </>
      )}
    </RoutedModal>
  );
};

TripEditPage.layout = (page: ReactNode) => page;

export default TripEditPage;
